// Type-only, so the mutual reference with authz/capabilities.ts (which imports
// UserRole from here) is erased at compile time — no runtime cycle.
import type { Capability } from '../authz/capabilities.js';

// Six preset roles (Brief 2c). `admin` replaces the former `agency_admin`.
export type UserRole =
  | 'super_admin'
  | 'admin'
  | 'owner'
  | 'consultant'
  | 'accounts'
  | 'offshore';

export const USER_ROLES: readonly UserRole[] = [
  'super_admin',
  'admin',
  'owner',
  'consultant',
  'accounts',
  'offshore',
] as const;

/**
 * The office a user belongs to. Null for super-admins and any user not yet
 * assigned to a branch.
 */
export type AuthBranch = {
  id: string;
  name: string;
  code: string;
};

/**
 * The user payload returned by the auth endpoints (login, refresh,
 * change-password, GET /auth/me). For super_admin, tenantId and branch are null.
 * Note: `branch` is resolved from the DB and is NOT baked into the JWT.
 */
export type AuthUser = {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  tenantId: string | null;
  branch: AuthBranch | null;
  /**
   * The user's EFFECTIVE capabilities — their role preset ± per-user overrides
   * (Brief 8a).
   *
   * ONE TYPE, TWO POPULATIONS — which is why this is optional:
   *  - DB-derived (`toAuthUser` → login / refresh / change-password / GET
   *    /auth/me): ALWAYS set. This is the copy the client receives, and the only
   *    thing that lets the UI reflect a capability switch at all.
   *  - Token-derived (`middleware/auth.ts` builds an AuthUser from JWT claims):
   *    ALWAYS omitted, because the access token carries no capabilities — exactly
   *    as `fullName` and `branch` are absent there today.
   *
   * Optional so the token-derived construction can keep telling the truth. A
   * required field would force `capabilities: []` there, which is a lie
   * indistinguishable from "this user has no capabilities".
   *
   * DECORATION, NOT A GATE. `requireCapability` (apps/api middleware/authz.ts)
   * reads overrides fresh from the DB on every gated request and remains the only
   * authority. This copy is refreshed at login/refresh, so it trails by up to the
   * access-token TTL by design. Never authorise from it server-side.
   */
  capabilities?: Capability[];
};
